import { lazy } from 'react';
import type { ComponentType } from 'react';

/**
 * Wraps React.lazy with a single page-reload retry on chunk load failure.
 *
 * After a deployment, old chunk URLs (with content hashes) no longer exist
 * on the CDN. When a user navigates to a lazy route for the first time
 * after a deploy, the import fails. This wrapper catches that error and
 * reloads the page once to pick up the new HTML with correct chunk refs.
 *
 * A sessionStorage flag prevents infinite reload loops.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = 'chunk-retry-reloaded';
    try {
      const module = await importFn();
      // Successful load — clear the retry flag
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      // Only retry once per session to avoid infinite loops
      if (!sessionStorage.getItem(storageKey)) {
        sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render the error
        return new Promise(() => {});
      }
      // Already retried — let the error propagate
      throw error;
    }
  });
}
